import { AFRICAN_COUNTRIES, AFRICAN_CULTURES } from "@/backend/lib/constants";
import {
  TIMELINE_MAX_YEAR,
  TIMELINE_MIN_YEAR,
  clampYear,
  isFullRange,
} from "@/backend/lib/timeline";

export const DEFAULT_KEYWORD_QUERY = "african artifacts";
export const DEFAULT_NAVIGATION_STATE: NavigationState = {
  artifactId: null,
  query: DEFAULT_KEYWORD_QUERY,
  filters: [...AFRICAN_COUNTRIES, ...AFRICAN_CULTURES],
  dateRange: { from: TIMELINE_MIN_YEAR, to: TIMELINE_MAX_YEAR },
};

export function resolveSemanticQuery(resolvedInput: string) {
  const trimmedQuery = resolvedInput.trim();
  const resolvedQuery = trimmedQuery || DEFAULT_KEYWORD_QUERY;
  const semanticQuery =
    trimmedQuery && trimmedQuery !== DEFAULT_KEYWORD_QUERY
      ? trimmedQuery
      : undefined;
  return { resolvedQuery, semanticQuery };
}

export type NavigationState = {
  artifactId?: string | null;
  query?: string;
  filters?: string[] | null;
  dateRange?: { from: number; to: number } | null;
};

type UpdateOptions = {
  replace?: boolean;
};

const parseHashParams = (): URLSearchParams =>
  new URLSearchParams(window.location.hash.replace(/^#/, ""));

const parseFiltersFromHash = (raw: string | null): string[] | null => {
  if (!raw) return null;

  try {
    const parsed = raw.split(",");
    return parsed;
  } catch (error) {
    console.warn("Failed to parse filters from hash", error);
  }

  return null;
};

/**
 * `from`/`to` in the hash, as plain signed years. Both must be present and
 * numeric; a half-written range from a hand-edited URL falls back to the whole
 * axis rather than filtering on a value nobody chose.
 */
const parseDateRangeFromHash = (
  rawFrom: string | null,
  rawTo: string | null,
): { from: number; to: number } | null => {
  if (rawFrom === null || rawTo === null) return null;
  const from = Number(rawFrom);
  const to = Number(rawTo);
  if (!Number.isFinite(from) || !Number.isFinite(to)) return null;
  const lo = clampYear(Math.min(from, to));
  const hi = clampYear(Math.max(from, to));
  return { from: lo, to: hi };
};

const writeHashParams = (params: URLSearchParams, replace = true) => {
  const hash = params.toString();
  const nextUrl = `${window.location.pathname}${window.location.search}${
    hash ? `#${hash}` : ""
  }`;

  if (replace) {
    history.replaceState(undefined, "", nextUrl);
  } else {
    window.location.hash = hash;
  }
};

export class HashNavigationStateManager {
  getState(): NavigationState {
    const params = parseHashParams();
    return {
      artifactId: params.get("artifact"),
      query: params.get("q") || "",
      filters: parseFiltersFromHash(params.get("filters")),
      dateRange: parseDateRangeFromHash(params.get("from"), params.get("to")),
    };
  }

  updateState(updates: NavigationState, options: UpdateOptions = {}) {
    const params = parseHashParams();
    console.log("updateState", updates, options, params);

    if ("artifactId" in updates) {
      const value = updates.artifactId;
      if (value && value !== DEFAULT_NAVIGATION_STATE.artifactId) {
        params.set("artifact", value);
      } else {
        params.delete("artifact");
      }
    }

    if ("query" in updates) {
      const value = updates.query;
      if (value && value !== DEFAULT_NAVIGATION_STATE.query) {
        params.set("q", value);
      } else {
        params.delete("q");
      }
    }

    if ("filters" in updates) {
      const value = updates.filters;
      const serializedValue = value?.join(",");
      const serializedDefault = DEFAULT_NAVIGATION_STATE.filters?.join(",");
      if (value && value.length > 0 && serializedValue !== serializedDefault) {
        params.set("filters", serializedValue || "");
      } else {
        params.delete("filters");
      }
    }

    if ("dateRange" in updates) {
      const value = updates.dateRange;
      // The full axis is the default, so it stays out of the URL — a shared
      // link should only carry the filters someone actually chose.
      if (value && !isFullRange(value.from, value.to)) {
        params.set("from", String(value.from));
        params.set("to", String(value.to));
      } else {
        params.delete("from");
        params.delete("to");
      }
    }

    writeHashParams(params, options.replace ?? true);
  }

  subscribe(callback: (state: NavigationState) => void) {
    const handler = () => callback(this.getState());
    window.addEventListener("hashchange", handler);
    return () => window.removeEventListener("hashchange", handler);
  }
}

export const useNavigationState = () => {
  return new HashNavigationStateManager();
};
